import { GlCollapsibleListbox } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { debounce, unionBy } from 'lodash-es';
import fuzzaldrinPlus from 'fuzzaldrin-plus';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { userIsDisabled } from '~/ai/agents_utils';
import SidebarParticipant from '/builds/gitlab/gitlab-ee/app/assets/javascripts/sidebar/components/assignees/sidebar_participant.vue?vue3';
import usersSearchQuery from '~/graphql_shared/queries/workspace_autocomplete_users.query.graphql';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { FLOW_TRIGGER_EVENTS } from '~/vue_shared/constants';
import { __, s__ } from '~/locale';
export default {
    name: 'DecisionLogDeciderSelect',
    components: {
        GlCollapsibleListbox,
        SidebarParticipant
    },
    props: {
        value: {
            type: String,
            required: false,
            default: ''
        },
        fullPath: {
            type: String,
            required: false,
            default: ''
        },
        isGroup: {
            type: Boolean,
            required: false,
            default: false
        },
        participants: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        selectedUser: {
            type: Object,
            required: false,
            default: null
        },
        isInvalid: {
            type: Boolean,
            required: false,
            default: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'input',
        'select-user'
    ],
    data () {
        return {
            searchKey: '',
            searchStarted: false,
            hasSearchFailed: false,
            users: []
        };
    },
    apollo: {
        users: {
            query: usersSearchQuery,
            variables () {
                return {
                    search: this.searchKey,
                    fullPath: this.fullPath,
                    isProject: !this.isGroup
                };
            },
            skip () {
                return !this.searchStarted || !this.fullPath;
            },
            update (data) {
                return (this.isGroup ? data.groupNamespace?.users : data.namespace?.users) ?? [];
            },
            error (error) {
                this.hasSearchFailed = true;
                Sentry.captureException(error);
            }
        }
    },
    computed: {
        // Participants come before search results so the dropdown lists them first.
        knownUsers () {
            return unionBy(this.seededUsers, this.participants, this.users, 'id');
        },
        seededUsers () {
            return this.selectedUser ? [
                this.selectedUser
            ] : [];
        },
        toggleText () {
            return this.knownUsers.find((user)=>user.id === this.value)?.name ?? s__('WorkItemDecisionLog|Select a user');
        },
        listedUsers () {
            if (this.searchKey === '') {
                return this.knownUsers;
            }
            return unionBy(this.matchedParticipants, this.users, 'id');
        },
        matchedParticipants () {
            return fuzzaldrinPlus.filter(this.participants.map((user)=>({
                    ...user,
                    matcher: `${user.name} ${user.username}`
                })), this.searchKey, {
                key: 'matcher'
            });
        },
        // The list item already shows these users as disabled, so they cannot be picked either. The
        // current decider stays selectable, so an edit does not lose it.
        items () {
            return this.listedUsers.map((user)=>({
                    value: user.id,
                    text: user.name,
                    user,
                    disabled: user.id !== this.value && userIsDisabled(user, FLOW_TRIGGER_EVENTS.ASSIGN)
                }));
        },
        isSearching () {
            return this.$apollo.queries.users.loading;
        },
        noResultsText () {
            return this.hasSearchFailed ? s__('WorkItemDecisionLog|Could not load users. Refresh the page and try again.') : s__('WorkItem|No matching results');
        },
        toggleClass () {
            return this.isInvalid ? '!gl-border-red-500' : '';
        }
    },
    created () {
        this.debouncedSearch = debounce((search)=>{
            this.hasSearchFailed = false;
            this.searchKey = search;
        }, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
    },
    beforeDestroy () {
        this.debouncedSearch.cancel();
    },
    methods: {
        // The list mixes participants with namespace search results, so only this component can turn
        // the chosen id back into a user.
        onSelect (id) {
            this.$emit('input', id);
            this.$emit('select-user', this.knownUsers.find((user)=>user.id === id) ?? null);
        },
        onShown () {
            this.hasSearchFailed = false;
            this.searchStarted = true;
        }
    },
    i18n: {
        searchPlaceholder: __('Search users')
    }
};
